import { useContext, useLayoutEffect, useMemo, useState } from "react";
import useAuth from "../../../../../../Hooks/useAuth";
import CustomizerContext from "../../../../../../_helper/Customizer";
import useAxiosPrivate from "../../../../../../Hooks/useAxiosPrivate";
import { PayrollPayHeadlineTypes } from "../types";
import { toast } from "react-toastify";
import {StatusRow} from "../../../../../Finance/Settings/ReportType/List/ReportTypeList";
import { formatDateWithTimeZone } from "../../../../../../_helper/dateHelper";
import { PayrollPayCategoryApi, PayrollPayHeadlineApi } from "../../../../../../api";
import {
  Name,
  Status,
  Description,
  DateCreated,
  DateModified,
  Alias,
  PayHeadline,
  Pensionable,
  Taxable,
  PayCategory,
} from "../../../../../../Constant";
import { createDropdownField } from "../../../../../../Data/FormField/PublicData";


export default function usePayrollPayHeadline() {
  const { userOrganisationId }: any = useAuth();
  const axiosPrivate = useAxiosPrivate();
  const title = PayHeadline;
  const [data, setData] = useState<PayrollPayHeadlineTypes[]>([]);
  const [formFields, setFormFields] = useState<any[]>([
    {
      id: "name",
      name: "name",
      label: Name,
      type: "text",
      value: "",
      required: true,
      colSize: 6,
    },
    {
      id: "short_name",
      name: "short_name",
      label: Alias,
      type: "text",
      value: "",
      required: true,
      colSize: 6,
    },

    {
      id: "description",
      name: "description",
      label: Description,
      type: "textarea",
      value: "",
      required: true,
      colSize: 6,
    },
    {
      id: "is_taxable",
      name: "is_taxable",
      label: Taxable,
      type: "switch",
      value: "1",
      colSize: 6,
    },
    {
      id: "is_pensionable",
      name: "is_pensionable",
      label: Pensionable,
      type: "switch",
      value: "1",
      colSize: 6,
    },
    {
      id: "is_active",
      name: "is_active",
      label: Status,
      type: "switch",
      value: "1",
      colSize: 6,
    },
  ]);

  const tableColumns = [
    {
      name: Name,
      selector: (row: (typeof data)[0]) => `${row.name}`,
      sortable: true,
      center: false,
    },
    {
      name: Alias,
      selector: (row: (typeof data)[0]) => `${row.short_name}`,
      sortable: true,
      center: false,
    },
    {
      name: Description,
      selector: (row: (typeof data)[0]) => `${row.description}`,
      sortable: true,
      center: false,
    },

    {
      name: Status,
      selector: (row: (typeof data)[0]) => `${row.is_active}`,
      sortable: true,
      center: true,
      cell: (row: (typeof data)[0]) => StatusRow(row),
    },
    {
      name: DateCreated,
      selector: (row: (typeof data)[0]) =>
        `${formatDateWithTimeZone(row.created_at)}`,
      sortable: false,
      center: false,
    },

    {
      name: DateModified,
      selector: (row: (typeof data)[0]) =>
        `${formatDateWithTimeZone(row.updated_at)}`,
      sortable: false,
      center: false,
    },
  ];

  const PayCategoryDropDown = createDropdownField(
      axiosPrivate,
      "payroll_pay_category_id",
      "payroll_pay_category_id",
      PayCategory,
      `${PayrollPayCategoryApi}?organisation_id=${userOrganisationId}`,
      "",
      6
  );

  // ?? Call on every dependent change
  async function updateFields() {
      const updatedFields = [PayCategoryDropDown, ...formFields];
      await PayCategoryDropDown.fetchOptions();
      setFormFields(updatedFields);
  }

  useLayoutEffect(() => {
    //?? Updates all fields once component is mounted
    updateFields();
  }, []);

  const { layoutURL } = useContext(CustomizerContext);
  const newBtn = `${process.env.PUBLIC_URL}/hrm/settings/grade-groups/create/${layoutURL}`;
  const [loading, setLoading] = useState(true);
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [selectedRowData, setSelectedRowData] = useState<
    (typeof data)[0] | null
  >(null);
  const [editMode, setEditMode] = useState(false);

  const fetchData = () => {
    setLoading(true);
    axiosPrivate
      .get(
        `${PayrollPayHeadlineApi}?skip=${0}&limit=${10}&with_trashed=${false}&organisation_id=${userOrganisationId}`
      )
      .then((res) => {
        const response: PayrollPayHeadlineTypes[] = res.data.data;
        setData(response);

        setLoading(false);
      })
      .catch((err) => {
        if (err.response && err.response.status === 404) {
          toast.error(err.response.data.detail);
        } else {
          toast.error("Whoops! Something went wrong");
        }
      })
      .finally(() => {
        setLoading(false);
      });
  };
  useLayoutEffect(() => {
    fetchData();
  }, []);

  //
  const handleEditRec = async (
    id: number | string,
    editRecord: (typeof data)[0] | any
  ) => {
    await axiosPrivate
      .put(`${PayrollPayHeadlineApi}/${id}`, {
        ...editRecord,
        organisation_id: userOrganisationId,
      })
      .then((_res) => {
        const updatedData = data.map((row) =>
          row.id === selectedRowData!.id ? { ...row, ..._res.data.data } : row
        );
        setData(updatedData);
        toast.success(`${_res.data.data.name} Editted Successfully.`);
      })
      .catch((err) => {
        if (err.response && err.response.status === 422) {
          toast.error("Validation Errors");
        } else if (err.response && err.response.status === 400) {
          toast.error(err.response.data.detail);
        } else {
          toast.error("Whoops! Something went wrong");
        }
        // throw err;
        toast.error(err.response.message || "Whoops! Something went wrong");
      });
  };
  const handleEdit = (row: (typeof data)[0] | any) => {
    setViewModalOpen(true);
    setEditMode(true);
    setSelectedRowData(row);
  };
  const memoizedHandleEdit = useMemo(
    () => handleEditRec,
    [data, selectedRowData]
  );

  const handleView = (row: (typeof data)[0]) => {
    setViewModalOpen(true);
    setEditMode(false);
    setSelectedRowData(row);
  };

  return {
    handleView,
    data,
    tableColumns,
    viewModalOpen,
    selectedRowData,
    editMode,
    newBtn,
    title,
    formFields,
    loading,
    setViewModalOpen,
    handleEdit,
    memoizedHandleEdit,
  };
}
